import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SENSORS_URL } from '../Constants';
import './PushButton.css';

const parseBoolean = (rawValue, fallback = false) => {
  if (typeof rawValue === 'boolean') return rawValue;
  if (rawValue === null || rawValue === undefined) return fallback;
  if (typeof rawValue === 'number') return rawValue !== 0;

  if (typeof rawValue === 'string') {
    const normalized = rawValue.trim().toLowerCase();
    if (!normalized) return fallback;
    if (['false', '0', 'no', 'off'].includes(normalized)) return false;
    if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
  }

  return Boolean(rawValue);
};

const normalizeLabel = (value, fallback) => {
  if (value === null || value === undefined) return fallback;
  const normalized = String(value).trim();
  return normalized || fallback;
};

const normalizeInteractionMode = (value) =>
  String(value || 'push').trim().toLowerCase() === 'switch' ? 'switch' : 'push';

const getFontSizeMultiplier = () => {
  if (typeof window === 'undefined') return 1;
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--font-size-multiplier');
  const parsed = parseFloat(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

const PushButton = ({
  title,
  transparentBackground,
  value,
  color,
  inactiveColor,
  textColor,
  inactiveTextColor,
  alignment,
  showLabel,
  onLabel,
  offLabel,
  interactionMode,
  draggable,
  pin,
  did,
  tid,
}) => {
  const containerRef = useRef(null);
  const titleRef = useRef(null);
  const [cursorState, setCursorState] = useState('idle');
  const [active, setActive] = useState(false);
  const [isHolding, setIsHolding] = useState(false);

  const canInteract = !draggable;
  const mode = useMemo(
    () => normalizeInteractionMode(interactionMode),
    [interactionMode]
  );
  const liveValue = parseBoolean(value, false);
  const shouldShowLabel = parseBoolean(showLabel, true);
  const activeLabel = normalizeLabel(onLabel, 'On');
  const inactiveLabel = normalizeLabel(offLabel, 'Off');
  const displayLabel = active ? activeLabel : inactiveLabel;

  useEffect(() => {
    if (mode === 'push' && isHolding) return;
    setActive(liveValue);
  }, [isHolding, liveValue, mode]);

  useEffect(() => {
    if (!containerRef.current) return;

    const resolvedAlignment = String(alignment || 'start').trim().toLowerCase();
    const justifyContentValue =
      resolvedAlignment === 'end' || resolvedAlignment === 'right'
        ? 'flex-end'
        : resolvedAlignment === 'center'
          ? 'center'
          : 'flex-start';
    containerRef.current.style.setProperty('--push-button-justify-content', justifyContentValue);

    if (color) {
      containerRef.current.style.setProperty('--push-button-active-bg', color);
      containerRef.current.style.setProperty('--push-button-active-border', color);
      containerRef.current.style.setProperty('--push-button-active-glow', color);
    } else {
      containerRef.current.style.removeProperty('--push-button-active-bg');
      containerRef.current.style.removeProperty('--push-button-active-border');
      containerRef.current.style.removeProperty('--push-button-active-glow');
    }

    if (inactiveColor) {
      containerRef.current.style.setProperty('--push-button-inactive-border', inactiveColor);
      containerRef.current.style.setProperty('--push-button-inactive-text', inactiveColor);
    } else {
      containerRef.current.style.removeProperty('--push-button-inactive-border');
      containerRef.current.style.removeProperty('--push-button-inactive-text');
    }

    if (textColor) {
      containerRef.current.style.setProperty('--push-button-active-text', textColor);
    } else {
      containerRef.current.style.removeProperty('--push-button-active-text');
    }

    if (inactiveTextColor) {
      containerRef.current.style.setProperty('--push-button-inactive-text', inactiveTextColor);
    } else if (inactiveColor) {
      containerRef.current.style.setProperty('--push-button-inactive-text', inactiveColor);
    } else {
      containerRef.current.style.removeProperty('--push-button-inactive-text');
    }
  }, [alignment, color, inactiveColor, inactiveTextColor, textColor]);

  useEffect(() => {
    if (!containerRef.current) return undefined;

    const element = containerRef.current;
    const updateResponsiveStyles = () => {
      const { width, height } = element.getBoundingClientRect();

      if (!width || !height) return;

      const fontSizeMultiplier = getFontSizeMultiplier();
      const titleHeight = titleRef.current?.getBoundingClientRect().height || 0;
      const hasVisibleTitle = titleHeight > 0;
      const titleSize = Math.max(
        20 * fontSizeMultiplier,
        Math.min(height * 0.12, 30 * fontSizeMultiplier)
      );
      const gap = Math.max(
        12 * fontSizeMultiplier,
        Math.min(height * 0.055, 18 * fontSizeMultiplier)
      );
      const rowPaddingInline = Math.max(
        0,
        Math.min(width * 0.055, 18 * fontSizeMultiplier)
      );
      const rowPaddingBlock = Math.max(
        4 * fontSizeMultiplier,
        Math.min(height * 0.04, 12 * fontSizeMultiplier)
      );
      const availableWidth = Math.max(width - rowPaddingInline * 2, 0);
      const availableHeight = Math.max(
        height - titleHeight - (hasVisibleTitle ? gap : 0) - rowPaddingBlock * 2,
        72 * fontSizeMultiplier
      );
      const minButtonHeight = Math.min(72 * fontSizeMultiplier, availableHeight);
      const buttonWidth = Math.max(
        Math.min(availableWidth, width * 0.92),
        Math.min(230 * fontSizeMultiplier, availableWidth)
      );
      const buttonHeight = Math.max(
        minButtonHeight,
        Math.min(availableHeight, availableHeight * 0.82)
      );
      const buttonRadius = Math.max(
        22 * fontSizeMultiplier,
        Math.min(buttonHeight * 0.34, 32 * fontSizeMultiplier)
      );
      const paddingInline = Math.max(
        24 * fontSizeMultiplier,
        Math.min(buttonWidth * 0.12, 36 * fontSizeMultiplier)
      );
      const paddingBlock = Math.max(
        14 * fontSizeMultiplier,
        Math.min(buttonHeight * 0.2, 22 * fontSizeMultiplier)
      );
      const labelFontSize = Math.max(
        24 * fontSizeMultiplier,
        Math.min(buttonHeight * 0.38, buttonWidth * 0.17, 40 * fontSizeMultiplier)
      );
      const labelSpacing = buttonWidth >= 320 ? '0.14em' : buttonWidth >= 260 ? '0.12em' : '0.1em';

      element.style.setProperty('--push-button-widget-gap', `${gap}px`);
      element.style.setProperty('--push-button-title-size', `${titleSize}px`);
      element.style.setProperty('--push-button-row-padding-inline', `${rowPaddingInline}px`);
      element.style.setProperty('--push-button-row-padding-block', `${rowPaddingBlock}px`);
      element.style.setProperty('--push-button-control-width', `${Math.round(buttonWidth)}px`);
      element.style.setProperty('--push-button-control-height', `${Math.round(buttonHeight)}px`);
      element.style.setProperty('--push-button-control-radius', `${Math.round(buttonRadius)}px`);
      element.style.setProperty('--push-button-control-padding-inline', `${Math.round(paddingInline)}px`);
      element.style.setProperty('--push-button-control-padding-block', `${Math.round(paddingBlock)}px`);
      element.style.setProperty('--push-button-label-size', `${Math.round(labelFontSize)}px`);
      element.style.setProperty('--push-button-label-spacing', labelSpacing);
    };

    updateResponsiveStyles();

    if (typeof ResizeObserver !== 'function') {
      window.addEventListener('resize', updateResponsiveStyles);
      return () => window.removeEventListener('resize', updateResponsiveStyles);
    }

    const resizeObserver = new ResizeObserver(() => updateResponsiveStyles());
    resizeObserver.observe(element);

    if (titleRef.current) {
      resizeObserver.observe(titleRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [title]);

  const sendControlValue = useCallback(
    async (nextValue, rollbackValue) => {
      setActive(nextValue);

      if (!did || !pin) {
        return;
      }

      const body = JSON.stringify({
        device: did,
        template: tid,
        data: { [pin]: Number(nextValue).toString() },
      });

      try {
        await axios.post(`${SENSORS_URL}/control`, body, {
          headers: { 'Content-Type': 'application/json' },
        });
      } catch (_error) {
        setActive(rollbackValue);
      }
    },
    [did, pin, tid]
  );

  const releasePushButton = useCallback(() => {
    setIsHolding((wasHolding) => {
      if (!wasHolding) return wasHolding;
      void sendControlValue(false, liveValue);
      return false;
    });
  }, [liveValue, sendControlValue]);

  useEffect(() => {
    if (!canInteract || mode !== 'push' || !isHolding) return undefined;

    const handlePointerRelease = () => releasePushButton();

    window.addEventListener('pointerup', handlePointerRelease);
    window.addEventListener('pointercancel', handlePointerRelease);
    window.addEventListener('blur', handlePointerRelease);

    return () => {
      window.removeEventListener('pointerup', handlePointerRelease);
      window.removeEventListener('pointercancel', handlePointerRelease);
      window.removeEventListener('blur', handlePointerRelease);
    };
  }, [canInteract, isHolding, mode, releasePushButton]);

  const handleMomentaryPress = useCallback(
    (event) => {
      if (!canInteract || mode !== 'push' || isHolding) return;
      event.preventDefault();
      event.stopPropagation();
      setIsHolding(true);
      void sendControlValue(true, liveValue);
    },
    [canInteract, isHolding, liveValue, mode, sendControlValue]
  );

  const handleToggleClick = useCallback(
    (event) => {
      if (!canInteract || mode !== 'switch') return;
      event.stopPropagation();
      void sendControlValue(!active, liveValue);
    },
    [active, canInteract, liveValue, mode, sendControlValue]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (mode !== 'push' || !canInteract) return;
      if (event.key !== ' ' && event.key !== 'Enter') return;
      handleMomentaryPress(event);
    },
    [canInteract, handleMomentaryPress, mode]
  );

  const handleKeyUp = useCallback(
    (event) => {
      if (mode !== 'push' || !canInteract) return;
      if (event.key !== ' ' && event.key !== 'Enter') return;
      event.preventDefault();
      event.stopPropagation();
      releasePushButton();
    },
    [canInteract, mode, releasePushButton]
  );

  const cursorClass = draggable
    ? cursorState === 'grabbing'
      ? 'push-button-widget--grabbing'
      : 'push-button-widget--grab'
    : 'push-button-widget--auto';

  return (
    <div
      ref={containerRef}
      className={[
        'widget-container',
        'push-button-widget',
        cursorClass,
        transparentBackground ? 'push-button-widget--transparent' : '',
      ].filter(Boolean).join(' ')}
      onMouseDown={() => draggable && setCursorState('grabbing')}
      onMouseUp={() => draggable && setCursorState('grab')}
      onMouseLeave={() => draggable && setCursorState('grab')}
      draggable={draggable}
      onDragStart={(event) => {
        const id = event.target.closest('.widget')?.id;
        if (!id) return;

        const rect = event.target.getBoundingClientRect();
        const offsetX = event.clientX - rect.x;
        const offsetY = event.clientY - rect.y;
        let dragData = `push-button;${id};${offsetX};${offsetY};`;
        dragData += id.startsWith('base') ? 'add' : 'move';
        event.dataTransfer.setData('text/plain', dragData);
      }}
    >
      <h3 ref={titleRef} className="push-button-widget__title">{title}</h3>

      <div className="push-button-widget__row">
        <button
          type="button"
          className={[
            'push-button-widget__button',
            active ? 'is-active' : 'is-inactive',
            mode === 'push' ? 'is-momentary' : 'is-latched',
          ].join(' ')}
          onClick={handleToggleClick}
          onPointerDown={handleMomentaryPress}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          aria-pressed={active}
        >
          {shouldShowLabel ? (
            <span className="push-button-widget__button-label">{displayLabel}</span>
          ) : null}
        </button>
      </div>
    </div>
  );
};

export default PushButton;
